import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { appInsights, logException } from '../../services/appInsights';
import RequiredInfoDialog from '../Shared/RequiredInfoDialog';
import { createPrintedList, titleize } from '../../util/stringUtil';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import GeneralInformation from './SeniorPlanning1Components/GeneralInformation';
import ExpandingList from '../ExpandingList/ExpandingList';
import FallPlan from './SeniorPlanning1Components/FallPlan';
import Scoring from './SeniorPlanning1Components/Scoring';
import { callApi } from '../../util/apiWrapper';
import { dateTimeUtcToS, getCurrentDateTime } from '../../util/dateUtil';
import { btNavyBlueColor, whiteColor } from '../../assets/jss/nextjs-material-kit-pro';
import PleaseWait from '../Shared/PleaseWait';
import useAuthRedirect from '../../hooks/authRedirectHook';
import { getPlanningMeeting1TotalScore, isScoringFieldsValid } from '../../util/scoringUtils';
import { displayStatus, isAdmin, isAdminOrFHT, isAlumni, isFHT, isStudent } from '../../util/miscUtil';
import { PLANNING_MEETING_FORM_VERSION } from '../../constants/general';
import { resolvePlanningFormVersion } from '../../util/formVersion';
import { trackApiRequestAudit } from '../../util/auditHelper';
import MeetingDiscussionDialog from '../PlanningMeetingDialogs/MeetingDiscussionDialog';
import { logAudit } from '../../util/auditUtil';
import { METHOD } from '../../constants/method';

const formStyles = makeStyles((theme) => ({
  subTitle: {
    margin: '15px 0',
    fontSize: '1.05rem',
    fontWeight: 500,
  },
  firstSectionSpacer: {
    height: 40,
  },
  sectionSpacer: {
    height: 60,
    padding: '10px 0',
  },
  rowSpacer: {
    height: 30,
  },
  textSpacer: {
    margin: '15px 0 5px 0',
  },
  smallRowSpacer: {
    margin: '3px 0 3px 0',
  },
  textRowSpacer: {
    [theme.breakpoints.up('sm')]: {
      height: 50,
    },
  },
  textArea5RowSpacer: {
    [theme.breakpoints.down('sm')]: {
      height: 85,
    },
    [theme.breakpoints.up('sm')]: {
      height: 75,
    },
  },
  textArea10RowSpacer: {
    height: 185,
  },
  labelRowSpacer: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: 15,
    },
    [theme.breakpoints.up('xs')]: {
      marginTop: 5,
    },
  },
  textRowLabelSpacer: {
    [theme.breakpoints.down('md')]: {
      height: 30,
      paddingTop: 5,
      overflow: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      height: 50,
      paddingTop: 8,
      overflow: 'auto',
    },
  },
  textRowLargeLabelSpacer: {
    [theme.breakpoints.down('sm')]: {
      height: 60,
      paddingTop: 5,
    },
    [theme.breakpoints.up('sm')]: {
      height: 50,
      paddingTop: 8,
    },
  },
  dialogHeaderFooter: {
    backgroundColor: btNavyBlueColor,
    color: whiteColor,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonWidth: {
    width: 110,
  },
  buttonSpacer: {
    height: 50,
    marginTop: 20,
  },
  statusRow: {
    fontWeight: 600,
    fontSize: '1.25rem',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '10px',
    },
    [theme.breakpoints.up('sm')]: {
      marginBottom: '2px',
    },
  },
  meetingDiscussionButton: {
    width: '140px',
  },
  firstSectionTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.10rem',
      fontWeight: 600,
    },
  },
  sectionTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.10rem',
      fontWeight: 600,
      margin: '15px 0',
    },
  },
  disabledSectionTitle: {
    color: 'gray',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.10rem',
      fontWeight: 600,
      margin: '15px 0',
    },
  },
  sectionSubTitle: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.80rem',
      fontWeight: 600,
      margin: '2px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '0.90rem',
      fontWeight: 600,
      margin: '5px 0',
    },
  },
  meetingDiscussionTitle: {
    color: btNavyBlueColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.20rem',
      fontWeight: 700,
      margin: '15px 5px',
    },
  },
  meetingDiscussionNotes: {
    color: btNavyBlueColor,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.00rem',
      margin: '15px 5px',
    },
  },
  hideForXs: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  hideForMd: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  radioLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    '& svg': {
      width: '1.25rem',
      height: '1.25rem',
    },
  },
  radioRow: {
    width: '100%',
    justifyContent: 'space-between',
  },
  datepicker: {
    width: '230px',
    '& .MuiFilledInput-root': {
      background: 'rgb(255, 255, 255)',
    },
  },
  datePickerRowSpacer: {
    height: 50,
    paddingTop: 8,
    marginBottom: 10,
  },
  reminder: {
    color: 'blue',
    fontSize: '1.05rem',
    margin: '10px 0',
  },
  [theme.breakpoints.down('xs')]: {
    textField: {
      fontSize: '0.75rem',
    },
    root: {
      '& .MuiFormLabel-root': {
        fontSize: '0.75rem',
      },
    },
  },
  bigLabel: {
    fontSize: '0.85rem',
  },
  addButton: {
    padding: 0,
    color: btNavyBlueColor,
  },
  topMargin10: {
    marginTop: 10,
  },
  printedContent: {
    '@media print': {
      marginBottom: 10,
    },
    '@media screen': {
      display: 'none',
    },
  },
  unprintedContent: {
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  alignPrintedContent: {
    lineHeight: '25px',
    verticalAlign: 'top',
  },
  commentSaveNote: {
    fontSize: '0.90rem',
    fontWeight: 600,
    color: 'blue',
  },
  error: {
    fontSize: '1.05rem',
    color: '#FA0000',
    fontWeight: 600,
  },
  spacer2520: {
    margin: '25px 0 20px 0',
  },
  dispositionNote: {
    fontSize: '1.05rem',
    padding: '15px 15px',
    margin: '20px 0',
    '@media print': {},
    '@media screen': {
      border: '1px solid blue',
      backgroundColor: '#FFFAF0',
    },
  },
  warning: {
    backgroundColor: '#fdb515',
  },
  errorAlert: {
    backgroundColor: '#fa0000',
    width: '900px',
  },
  note: {
    color: 'blue',
  },
  submittedInfo: {
    color: btNavyBlueColor,
    fontStyle: 'italic',
    fontSize: '0.95rem',
    fontWeight: 600,
  },
  meetingSaveNote: {
    fontSize: '0.90rem',
    fontWeight: 600,
    color: 'blue',
  },
  floatingSaveButton: {
    width: 210,
    position: 'absolute',
    right: 15,
    bottom: 15,
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  floatingSubmitButton: {
    width: 210,
    position: 'absolute',
    left: 15,
    bottom: 15,
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  space10After: {
    marginBottom: 10,
  },
  totalScore: {
    color: btNavyBlueColor,
    fontSize: '1.00rem',
    fontWeight: 600,
    paddingBottom: 25,
  },
  discussionTooltip: {
    paddingTop: '15px',
    width: 530,
    maxWidth: 530,
  },
}));

const NAME = 'Meeting#1';
const interestKeys = ['interest1', 'interest2', 'interest3'];

const SeniorPlanning1 = inject(
  'SeniorStore',
  'AuthStore'
)(
  observer((props) => {
    const store = props.SeniorStore;
    const auth = props.AuthStore;

    const commentRef = useRef();
    const meetingCommentRef = useRef();

    const { handleSaveReturn } = props;

    const formClasses = formStyles();

    const [addMeetingNote, setAddMeetingNote] = useState('');

    const [requiredFields, setRequiredFields] = useState(null);
    const [requiredFieldsTitle, setRequiredFieldsTitle] = useState('');
    const [optionalFieldOnly, setOptionalFieldOnly] = useState(false);
    const [requiredFieldsIntro, setRequiredFieldsIntro] = useState('');
    const [showRequiredFieldsDialog, setShowRequiredFieldsDialog] = useState(false);
    const [showMeetingDiscussionDialog, setShowMeetingDiscussionDialog] = useState(false);
    const [meetingDiscussionItemSubmittedAttempted, setMeetingDiscussionItemSubmittedAttempted] = useState(false);

    const [userList, setUserList] = useState([]);

    const [originalStatus, setOriginalStatus] = useState('');

    const [showIncomplete, setShowIncomplete] = useState(false);

    const [warning, setWarning] = useState('');
    const [showWarning, setShowWarning] = useState(false);

    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);

    const [fetchSuccess, setFetchSuccess] = useState(false);

    const isSavingRef = useRef(false);

    const isFieldDisabled = () => {
      if (!isStudent(auth.roles) && !isAlumni(auth.roles)) return true;
      return form.status === 'submitted' || form.status === 'approved' || form.status === 'incomplete';
    };

    const handleClose = (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }
      setShowWarning(false);
    };

    const handleErrorClose = (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }
      setShowError(false);
    };

    const action = (
      <React.Fragment>
        <IconButton size='small' aria-label='close' color='inherit' onClick={handleClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </React.Fragment>
    );

    const errorAction = (
      <React.Fragment>
        <IconButton size='small' aria-label='close' color='inherit' onClick={handleErrorClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </React.Fragment>
    );

    // prettier-ignore
    const [form, setForm] = useState({
      interestsPostGraduation: '',
      interest1: '',
      interest2: '',
      interest3: '',
      fallPlanA: '',
      planDescriptionA: '',
      jobRequirementsA: '',
      educationTrainingA: '',
      educationTrainingCostA: '',
      paymentPlanA: '',
      goodPlanA: '',
      barriersPlanA: '',
      fallPlanB: '',
      planDescriptionB: '',
      jobRequirementsB: '',
      educationTrainingB: '',
      educationTrainingCostB: '',
      paymentPlanB: '',
      goodPlanB: '',
      barriersPlanB: '',
      fallPlanC: '',
      planDescriptionC: '',
      jobRequirementsC: '',
      educationTrainingC: '',
      educationTrainingCostC: '',
      paymentPlanC: '',
      goodPlanC: '',
      barriersPlanC: '',
      comments: [],
      tasks: [{ name: '' }, { name: '' }, { name: '' }, { name: '' }, { name: '' }],
      status: 'created',
      dispositionReason: '',
      submitted_date: null,
      followup: false,
      gradingSheet1Completed: -1,
      gradingSheet1Submitted: -1,
      gradingSheet1Clarity: -1,
      module1Submitted: -1,
      module1Completed: -1,
      module2Submitted: -1,
      module2Completed: -1,
      module3Submitted: -1,
      module3Completed: -1,
      meetingComments: [],
      rubricMotivation: 0,
      rubricMotivationExplain: '',
      rubricFuture: 0,
      rubricFutureExplain: '',
      rubricSFTeam: 0,
      rubricSFTeamExplain: '',
      rubricMotivationScore: 0,
      rubricSuccess: 0,
      version: PLANNING_MEETING_FORM_VERSION,
      careerInterests: {
        interest1: {
          program: '',
          careerInterest: '',
          educationOrTrainingNeeded: '',
          twoThingsILikeAboutThisCareer: '',
          twoChallengesorDownsides: '',
          whyThisCareerAppealstoMe: ''
        },
        interest2: {
          program: '',
          careerInterest: '',
          educationOrTrainingNeeded: '',
          twoThingsILikeAboutThisCareer: '',
          twoChallengesorDownsides: '',
          whyThisCareerAppealstoMe: ''
        },
        interest3: {
          program: '',
          careerInterest: '',
          educationOrTrainingNeeded: '',
          twoThingsILikeAboutThisCareer: '',
          twoChallengesorDownsides: '',
          whyThisCareerAppealstoMe: ''
        }
      },
      careerInterestsReason: ''
    });

    const [discussionNotes, setDiscussionNotes] = useState('');
    const [originalDiscussionNotes, setOriginalDiscussionNotes] = useState('');
    const [discussionNoteUpdates, setDiscussionNoteUpdates] = useState([]);

    const formRef = useRef(form);
    useEffect(() => {
      formRef.current = form;
    }, [form]);

    const [addNote, setAddNote] = useState('');

    const buildPlanningForm1Item = (planningFormData, userId = auth.user.result.id) => ({
      student_id: store.senior.id,
      created_at: new Date(),
      created_by: userId,
      updated_at: new Date(),
      updated_by: userId,
      planning_form_1_data: planningFormData,
    });

    const auditedPlanningForm1Request = async ({ item, action, requestMethod }) => {
      try {
        await trackApiRequestAudit({
          auth,
          payload: { item },
          action,
          auditType: NAME,
          requestMethod,
        });
      } catch (auditError) {
        logException(auditError);
        console.log('Error auditing Planning Form 1 request:', auditError);
      }
      return callApi(auth, 'planningform1', requestMethod, { item });
    };

    const writeSeniorPlan1 = async (closeInd, currentFormData = formRef.current) => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;

      try {
        store.setLoading(false);

        // Send email
        if (originalStatus !== 'returned' && currentFormData.status === 'returned') {
          await sendPlanningForm1ReturnedEmail(store.senior.id);
        }

        let updatedPlanningForm1 = null;

        if (store.planform1Obj && store.planform1Obj.id && store.planform1Obj.id > 0) {
          updatedPlanningForm1 = {
            ...store.planform1Obj,
            planning_form_1_data: currentFormData,
            updated_at: new Date(),
            updated_by: auth.user.result.id,
          };
        }

        if (updatedPlanningForm1) {
          await auditedPlanningForm1Request({
            item: updatedPlanningForm1,
            action: 'save',
            requestMethod: METHOD.PUT,
          });
        } else {
          store.setLoading(true);
          const createPlanningForm1Item = buildPlanningForm1Item(currentFormData);
          const res = await auditedPlanningForm1Request({
            item: createPlanningForm1Item,
            action: 'save',
            requestMethod: METHOD.POST,
          });

          if (res.result) {
            updatedPlanningForm1 = res.result;
            store.setPlanform1Obj(updatedPlanningForm1);
            setFetchSuccess(true);
          }
        }

        appInsights.trackTrace({ message: `Planning Form 1 saved by ${auth.user.result.email}` });

        store.setLoading(false);
        closeInd && handleSaveReturn();
      } catch (error) {
        logException(error);
        store.setLoading(false);
        setError('Failed to save your Planning Form. Please try again or check your network connection.');
        setShowError(true);
        console.log('Error writing updated planning form 1:', error);
      } finally {
        isSavingRef.current = false;
      }
    };

    useAuthRedirect(auth);

    useEffect(() => {
      window.scrollTo({ top: 0 });

      const fetchPlanningForm1 = async (studentId) => {
        let planningFormId;
        try {
          store.setLoading(true);
          store.setSearching(true);

          const planningform1 = await callApi(auth, 'planningform1/' + studentId, 'GET');

          if (planningform1 && planningform1.result && planningform1.result.id > 0) {
            store.setPlanform1Obj(planningform1.result);
            planningFormId = planningform1.result.id;
            setFetchSuccess(true);

            // Safe parsing in case data is returned as object or string
            let adjustForm;
            try {
              adjustForm =
                typeof planningform1.result.planning_form_1_data === 'string'
                  ? JSON.parse(planningform1.result.planning_form_1_data)
                  : planningform1.result.planning_form_1_data || {};
            } catch (parseErr) {
              console.error('Failed to parse planning_form_1_data JSON:', parseErr);
              appInsights.trackTrace({ message: `Failed to parse planning_form_1_data JSON: ${parseErr}` });
              adjustForm = {};
            }

            // Migrate v1 top-level string interests to v2 careerInterests if present
            const normalizeCareerInterests = (rawForm = {}) => {
              const defaultFields = {
                program: '',
                careerInterest: '',
                educationOrTrainingNeeded: '',
                twoThingsILikeAboutThisCareer: '',
                twoChallengesorDownsides: '',
                whyThisCareerAppealstoMe: '',
              };
              const existingCareerInterests = rawForm.careerInterests || {};
              const normalized = {};

              interestKeys.forEach((key) => {
                const legacyString = rawForm[key] || '';
                const existingObj = existingCareerInterests[key] || {};

                normalized[key] = {
                  ...defaultFields,
                  ...existingObj,
                  careerInterest: existingObj.careerInterest || legacyString,
                };
              });
              return normalized;
            };

            adjustForm.careerInterests = normalizeCareerInterests(adjustForm);

            if (!adjustForm.hasOwnProperty('tasks')) {
              adjustForm.tasks = [{ name: '' }, { name: '' }, { name: '' }, { name: '' }, { name: '' }];
            }
            if (!adjustForm.hasOwnProperty('status')) {
              adjustForm.status = 'created';
            }
            if (!adjustForm.hasOwnProperty('meetingComments')) {
              adjustForm.meetingComments = [];
            }

            if (!adjustForm.hasOwnProperty('rubricMotivation')) {
              adjustForm.rubricMotivation = 0;
            }
            if (!adjustForm.hasOwnProperty('rubricFuture')) {
              adjustForm.rubricFuture = 0;
            }
            if (!adjustForm.hasOwnProperty('rubricSFTeam')) {
              adjustForm.rubricSFTeam = 0;
            }
            if (!adjustForm.hasOwnProperty('rubricMotivationScore')) {
              adjustForm.rubricMotivationScore = 0;
            } else {
              adjustForm.rubricMotivationScore = (adjustForm.rubricMotivation + adjustForm.rubricFuture + adjustForm.rubricSFTeam) / 3;
            }
            if (!adjustForm.hasOwnProperty('rubricSuccess')) {
              adjustForm.rubricSuccess = 0;
            }

            adjustForm.version = resolvePlanningFormVersion(adjustForm.version, planningform1.result?.version, PLANNING_MEETING_FORM_VERSION);

            setOriginalStatus(adjustForm.status);
            setForm(adjustForm);
          } else {
            const createdFormData = {
              ...form,
              status: 'created',
              version: PLANNING_MEETING_FORM_VERSION,
            };
            setForm(createdFormData);
            setOriginalStatus('created');
            setFetchSuccess(true);

            try {
              const createPlanningForm1Item = buildPlanningForm1Item(null);
              const createResult = await auditedPlanningForm1Request({
                item: createPlanningForm1Item,
                action: 'save',
                requestMethod: METHOD.POST,
              });

              if (createResult && createResult.result) {
                const createdRecord = createResult.result;
                const persistedFormData = {
                  ...createdFormData,
                  status: createdFormData.status || 'created',
                  version: resolvePlanningFormVersion(createdFormData.version, createdRecord.version, PLANNING_MEETING_FORM_VERSION),
                };

                const updatedPlanningForm1 = {
                  ...createdRecord,
                  planning_form_1_data: persistedFormData,
                  updated_at: new Date(),
                  updated_by: auth.user.result.id,
                };

                const putResult = await auditedPlanningForm1Request({
                  item: updatedPlanningForm1,
                  action: 'save',
                  requestMethod: METHOD.PUT,
                });

                store.setPlanform1Obj(putResult?.result || updatedPlanningForm1);
                planningFormId = putResult?.result?.id || createdRecord.id;
                setForm(persistedFormData);
                setOriginalStatus(persistedFormData.status || 'created');
              }
            } catch (createError) {
              logException(createError);
              console.log('Error creating Planning Form 1 new record:', createError);
              appInsights.trackTrace({ message: `Error creating Planning Form 1 new record: ${createError}` });
            }
          }
          store.setSearching(false);
          store.setLoading(false);
          return planningFormId;
        } catch (error) {
          setFetchSuccess(false);
          store.setSearching(false);
          store.setLoading(false);
          setError('Your Planning Form 1 did not load successfully. Please close your browser and retry. Failure to do so may result in data loss!');
          appInsights.trackTrace({ message: 'Planning Form 1 data failed to load. Error displayed to user.' });
          setShowError(true);
          logException(error);
        }
      };

      const fetchPlanningForm1DiscussionNotes = async (studentId) => {
        const fetchedDiscussion = await callApi(auth, 'planningform1/discussion/student/' + studentId, 'GET');
        if (fetchedDiscussion && fetchedDiscussion.result && fetchedDiscussion.result.discussion_notes.length > 0) {
          setDiscussionNotes(fetchedDiscussion.result.discussion_notes);
        }
      };

      const fetchUserList = async () => {
        const userList = await callApi(auth, 'user/list', 'GET');
        if (userList && userList.result && userList.result.length > 0) {
          setUserList(userList.result);
        }
      };

      const fetchPlanningForm1DiscussionUpdates = async (planningFormId) => {
        if (!planningFormId) return;
        try {
          const discussionUpdates = await callApi(auth, 'planningform1/discussion/form/' + planningFormId, 'GET');
          if (discussionUpdates && discussionUpdates.results && discussionUpdates.results.length > 0) {
            setDiscussionNoteUpdates(discussionUpdates.results);
          }
        } catch (discussionUpdatesError) {
          logException(discussionUpdatesError);
          console.log('Error fetching Planning Form 1 discussion updates:', discussionUpdatesError);
        }
      };

      if (store.senior.id > 0) {
        fetchUserList();
        fetchPlanningForm1DiscussionNotes(store.senior.id);
        // Wait for the resolved planning form id to avoid reading a stale/unset store value
        fetchPlanningForm1(store.senior.id).then((planningFormId) => fetchPlanningForm1DiscussionUpdates(planningFormId));
      }
    }, [auth, store]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      const isEditable = formRef.current.status === 'created' || formRef.current.status === 'returned';

      if ((isStudent(auth.roles) || isAlumni(auth.roles)) && fetchSuccess && isEditable) {
        const interval = setInterval(() => {
          handleSavePlanningForm1(false, formRef.current);
          appInsights.trackTrace({ message: `Auto saved Planning Form 1 for ${auth.user.result.email}` });
        }, 300000);

        return () => clearInterval(interval);
      }
    }, [auth, fetchSuccess, form.status]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddMeetingDiscussionItem = async () => {
      try {
        store.setLoading(false);

        const item = {
          planning_meeting_1_id: store.planform1Obj.id,
          student_id: store.senior.id,
          discussion_notes: discussionNotes,
          created_at: getCurrentDateTime(),
          created_by: auth.user.result.id,
          modified_at: getCurrentDateTime(),
          modified_by: auth.user.result.id,
        };

        const newNote = await callApi(auth, 'planningform1/discussion', 'POST', { item: item });
        if (newNote && newNote.result) {
          setDiscussionNoteUpdates((prev) => [newNote.result, ...(Array.isArray(prev) ? prev : [])]);
        }

        const auditObj = {
          audit_ts: new Date(),
          user_id: auth.user.result.id,
          username: auth.user.result.first_name + ' ' + auth.user.result.last_name,
          audit_type: 'access request',
          action: 'discussion update',
          value: JSON.stringify(item).length > 999 ? JSON.stringify(item).substring(0, 999) : JSON.stringify(item),
        };
        logAudit(auth, auditObj);

        appInsights.trackTrace({ message: `PlanningForm1 Discussion item submitted by ${auth.user.result.email}` });

        store.setLoading(false);
      } catch (error) {
        logException(error);
        store.setLoading(false);
        setError('Failed to submit Planning Form 1 Discussion item. Please try again or check your network connection.');
        setShowError(true);
        console.log('Error submitting Planning Form 1 Discussion item:', error);
      } finally {
        // handleMeetingDiscussionClose();
        setShowMeetingDiscussionDialog(false);
      }
    };

    const handleAddComment = () => {
      const updatedComments = [
        ...form.comments,
        {
          timestamp: getCurrentDateTime(),
          text: addNote,
          user: auth.user.result.first_name + ' ' + auth.user.result.last_name,
        },
      ];
      setForm({ ...form, comments: updatedComments });
      setAddNote('');
    };

    const handleAddMeetingComment = () => {
      const updatedMeetingComments = [
        ...form.meetingComments,
        {
          timestamp: getCurrentDateTime(),
          text: addMeetingNote,
          user: auth.user.result.first_name + ' ' + auth.user.result.last_name,
        },
      ];
      setForm({ ...form, meetingComments: updatedMeetingComments });
      setAddMeetingNote('');
    };

    const sendPlanningForm1ReturnedEmail = async (id) => {
      try {
        store.setLoading(true);
        await callApi(auth, 'planningform1returnemail', 'POST', { student_id: id });
        appInsights.trackTrace({ message: `PlanningForm1 returned email sent to ${auth.user.result.email}` });
        store.setLoading(false);
      } catch (error) {
        logException(error);
        store.setLoading(false);
        console.log('Error sending Planning Form 1 returned email:', error);
      }
    };

    const handleSavePlanningForm1 = (closeInd, currentFormData = formRef.current) => {
      if (!fetchSuccess) {
        setError(
          'Your Planning Form 1 cannot be saved, because it was not successfully loaded. Please close your browser and retry. Failure to do so may result in data loss!'
        );
        setShowError(true);
        appInsights.trackTrace({ message: 'Planning Form 1 data failed to load. Error displayed to user on save.' });
        return;
      }

      if (isAdmin(auth.roles) && canDisposition() === false) {
        setShowRequiredFieldsDialog(true);
        return;
      }

      if (isAdmin(auth.roles) && currentFormData.status === 'returned') {
        if (!currentFormData.dispositionReason || currentFormData.dispositionReason.length === 0) {
          setWarning('You must provide a reason for returning the planning form.');
          setShowWarning(true);
          return;
        }
      }

      writeSeniorPlan1(closeInd, currentFormData);
    };

    const handleSubmitPlanningForm1 = () => {
      if (!fetchSuccess) {
        setError(
          'Your Planning Form 1 cannot be submitted, because it was not successfully loaded. Please close your browser and retry. Failure to do so may result in data loss!'
        );
        setShowError(true);
        appInsights.trackTrace({ message: 'Planning Form 1 data failed to load. Error displayed to user on submit.' });
        return;
      }

      if (canSubmit()) {
        submitPlanningFormContinue();
      } else {
        setShowRequiredFieldsDialog(true);
      }
    };

    const submitPlanningFormContinue = async () => {
      if (!fetchSuccess) {
        setError(
          'Your Planning Form 1 cannot be saved, because it was not successfully loaded. Please close your browser and retry. Failure to do so may result in data loss!'
        );
        setShowError(true);
        return;
      }

      // If an auto-save operation is currently in flight, wait up to 3 seconds before continuing
      let retries = 0;
      while (isSavingRef.current && retries < 6) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        retries += 1;
      }

      if (isSavingRef.current) return;
      isSavingRef.current = true;

      try {
        store.setLoading(false);

        let updatedPlanningForm1 = null;
        const submittedForm = {
          ...formRef.current,
          status: 'submitted',
          dispositionReason: '',
          submitted_date: getCurrentDateTime(),
        };

        if (store.planform1Obj && store.planform1Obj.id && store.planform1Obj.id > 0) {
          updatedPlanningForm1 = {
            ...store.planform1Obj,
            planning_form_1_data: submittedForm,
            updated_at: new Date(),
            updated_by: auth.user.result.id,
          };

          await auditedPlanningForm1Request({
            item: updatedPlanningForm1,
            action: 'submit',
            requestMethod: 'PUT',
          });
        } else {
          store.setLoading(true);

          const createPlanningForm1Item = buildPlanningForm1Item(submittedForm);
          const res = await auditedPlanningForm1Request({
            item: createPlanningForm1Item,
            action: 'submit',
            requestMethod: 'POST',
          });
          if (res.result) {
            updatedPlanningForm1 = res.result;
            store.setPlanform1Obj(updatedPlanningForm1);
          }
        }

        appInsights.trackTrace({ message: `PlanningForm1 submitted by ${auth.user.result.email}` });

        store.setLoading(false);
        handleSaveReturn();
      } catch (error) {
        logException(error);
        store.setLoading(false);
        setError('Failed to submit Planning Form 1. Please try again or check your network connection.');
        setShowError(true);
        console.log('Error submitting Planning Form 1:', error);
      } finally {
        isSavingRef.current = false;
      }
    };

    const canSubmit = () => {
      let rc = true;
      let requiredFields = [];
      const senior = store.senior || {};

      // Fixed ACT / ASVAB score checks to avoid null pointer exceptions
      const hasAct = senior.act !== undefined && senior.act !== null && senior.act !== '';
      const hasAsvab = senior.asvab !== undefined && senior.asvab !== null && senior.asvab !== 0 && senior.asvab !== '';

      if (!hasAct || !hasAsvab) {
        requiredFields = [
          ...requiredFields,
          {
            requirement:
              'If you have taken the ACT/ASVAB, please enter your score on your personal information page in order to be included on this form.',
          },
        ];
        rc = false; // Optional field warning trigger
      }

      if (form.version === 1 && (!form.interestsPostGraduation || form.interestsPostGraduation.length === 0)) {
        requiredFields = [...requiredFields, { requirement: 'Indicate your post-graduation plans before submitting your Planning Form #1.' }];
        rc = false;
      }

      let totalInterestsEntered = 0;
      if (!form.version || form.version === 1) {
        if (form.interest1) totalInterestsEntered += 1;
        if (form.interest2) totalInterestsEntered += 1;
        if (form.interest3) totalInterestsEntered += 1;
      }

      if (form.version === 2 && form.careerInterests) {
        ['interest1', 'interest2', 'interest3'].forEach((key) => {
          const interest = form.careerInterests[key] || {};
          // Count interest if careerInterest is filled out, or if all detailed fields are populated
          if (
            interest.careerInterest ||
            (interest.educationOrTrainingNeeded &&
              interest.twoThingsILikeAboutThisCareer &&
              interest.twoChallengesorDownsides &&
              interest.whyThisCareerAppealstoMe)
          ) {
            totalInterestsEntered += 1;
          }
        });
      }

      if (totalInterestsEntered < 2) {
        requiredFields = [...requiredFields, { requirement: 'At least 2 career interests must be entered.' }];
        rc = false;
      }

      //Plan A
      if (!form.fallPlanA || form.fallPlanA.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Plan A is required.' }];
        rc = false;
      }

      if (!form.planDescriptionA || form.planDescriptionA.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Please describe Plan A.' }];
        rc = false;
      }

      if (!form.jobRequirementsA || form.jobRequirementsA.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure requirements needed to complete Plan A are entered.' }];
        rc = false;
      }

      if (!form.educationTrainingA || form.educationTrainingA.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure education/training location for Plan A is entered.' }];
        rc = false;
      }

      if (!form.educationTrainingCostA || form.educationTrainingCostA.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure education/training cost for Plan A is entered.' }];
        rc = false;
      }

      if (!form.paymentPlanA || form.paymentPlanA.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure payment plan for Plan A is entered.' }];
        rc = false;
      }

      if (!form.goodPlanA || form.goodPlanA.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Please enter a reason why you think Plan A is a good fit for you.' }];
        rc = false;
      }

      if (!form.barriersPlanA || form.barriersPlanA.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure any barriers for Plan A are entered.' }];
        rc = false;
      }

      //Plan B
      if (!form.fallPlanB || form.fallPlanB.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Plan B is required.' }];
        rc = false;
      }

      if (!form.planDescriptionB || form.planDescriptionB.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Please describe Plan B.' }];
        rc = false;
      }

      if (!form.jobRequirementsB || form.jobRequirementsB.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure requirements needed to complete Plan B are entered.' }];
        rc = false;
      }

      if (!form.educationTrainingB || form.educationTrainingB.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure education/training location for Plan B is entered.' }];
        rc = false;
      }

      if (!form.educationTrainingCostB || form.educationTrainingCostB.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure education/training cost for Plan B is entered.' }];
        rc = false;
      }

      if (!form.paymentPlanB || form.paymentPlanB.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure payment plan for Plan B is entered.' }];
        rc = false;
      }

      if (!form.goodPlanB || form.goodPlanB.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Please enter a reason why you think Plan B is a good fit for you.' }];
        rc = false;
      }

      if (!form.barriersPlanB || form.barriersPlanB.length === 0) {
        requiredFields = [...requiredFields, { requirement: 'Ensure any barriers for Plan B are entered.' }];
        rc = false;
      }

      //Plan C - if Plan C is specified, all fields under plan C are required
      if (form.fallPlanC && form.fallPlanC.length > 0) {
        if (!form.planDescriptionC || form.planDescriptionC.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please describe Plan C.' }];
          rc = false;
        }

        if (!form.jobRequirementsC || form.jobRequirementsC.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Ensure requirements needed to complete Plan C are entered.' }];
          rc = false;
        }

        if (!form.educationTrainingC || form.educationTrainingC.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Ensure education/training location for Plan C is entered.' }];
          rc = false;
        }

        if (!form.educationTrainingCostC || form.educationTrainingCostC.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Ensure education/training cost for Plan C is entered.' }];
          rc = false;
        }

        if (!form.paymentPlanC || form.paymentPlanC.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Ensure payment plan for Plan C is entered.' }];
          rc = false;
        }

        if (!form.goodPlanC || form.goodPlanC.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please enter a reason why you think Plan C is a good fit for you.' }];
          rc = false;
        }

        if (!form.barriersPlanC || form.barriersPlanC.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Ensure any barriers for Plan C are entered.' }];
          rc = false;
        }
      }

      let totalTasksEntered = 0;
      if (form.tasks && form.tasks.length > 0) {
        for (let i = 0; i < form.tasks.length; i++) {
          const task = form.tasks[i];
          if (task.name && task.name.length > 0 && task.name !== '') {
            totalTasksEntered += 1;
          }
        }
        if (totalTasksEntered < 3) {
          requiredFields = [...requiredFields, { requirement: 'You do not have enough tasks listed. Ensure at least 3 tasks have been entered.' }];
          rc = false;
        }
      }

      if (requiredFields.length === 1 && requiredFields[0].requirement.includes('ACT')) {
        setOptionalFieldOnly(true);
        setRequiredFieldsTitle('Planning Form #1 Warning');
        setRequiredFieldsIntro('You have some missing information on your Planning Form #1.');
      } else {
        setOptionalFieldOnly(false);
        setRequiredFieldsTitle('Planning Form #1 Errors');
        setRequiredFieldsIntro(
          'You have not completed all of the required information on your Planning Form #1. Please make these adjustments before submitting.'
        );
      }

      setRequiredFields(requiredFields);
      return rc;
    };

    const handleRequiredInfoClose = () => {
      setShowRequiredFieldsDialog(false);
    };

    const handleMeetingDiscussionClose = () => {
      setDiscussionNotes(originalDiscussionNotes);
      setShowMeetingDiscussionDialog(false);
    };

    const editDiscussionNotes = (val) => {
      setOriginalDiscussionNotes(discussionNotes);
      setShowMeetingDiscussionDialog(val);
    };

    const handleRequiredInfoContinue = () => {
      setShowRequiredFieldsDialog(false);
      submitPlanningFormContinue();
    };

    const showSaveButton = () => {
      // Status Values: submitted, returned, approved
      if (isAdminOrFHT(auth.roles)) {
        return true;
      }

      if (isStudent(auth.roles) || isAlumni(auth.roles)) {
        if (form.status === 'created' || form.status === 'returned') {
          return true;
        }
      }
      return false;
    };

    const showDisposition = () => {
      let rc = false;
      if (isAdmin(auth.roles)) {
        // Status Values: created, submitted, approved, returned
        if (originalStatus === 'submitted' || originalStatus === 'incomplete' || showIncomplete) {
          rc = true;
        }
      }
      return rc;
    };

    const shouldDisplayStatusNotes = () => {
      return !!(form.dispositionReason && form.dispositionReason.length > 0);
    };

    const displayUserName = (userId) => {
      const user = userList.find((u) => u.id === parseInt(userId, 10));
      return user ? `${user.first_name} ${user.last_name}` : 'N/A';
    };

    const formatDiscussionUpdateData = (data) => {
      if (!data || !Array.isArray(data)) return '';
      return (
        <>
          <div style={{ fontWeight: 'bold', margin: '10px 0 20px 0', fontSize: '1.2rem' }}>Change History</div>
          {data.map((item, index) => (
            <div key={index}>
              <ul style={{ margin: '0 0 15px 0', fontSize: '0.95rem' }}>
                <li>
                  <strong>Updated by</strong> {displayUserName(item.modified_by)} <strong>at</strong> {dateTimeUtcToS(item.modified_at) || 'N/A'}
                </li>
              </ul>
            </div>
          ))}
        </>
      );
    };

    const markFormIncomplete = () => {
      setShowIncomplete(!showIncomplete);
      setForm({ ...form, status: 'incomplete' });
    };

    const canDisposition = () => {
      let rc = true;
      let requiredFields = [];

      if (form.status === 'approved') {
        if (!isScoringFieldsValid(form)) {
          requiredFields = [
            ...requiredFields,
            { requirement: 'You must score the application before approving. Please score each item before changing the status to approved.' },
          ];
          rc = false;
        }
      }

      setRequiredFieldsIntro('Please make these adjustments before continuing.');
      setRequiredFields(requiredFields);
      return rc;
    };

    return (
      <React.Fragment>
        <PleaseWait isLoading={store.loading} />
        {showMeetingDiscussionDialog && (
          <Dialog
            open={showMeetingDiscussionDialog}
            onClose={handleMeetingDiscussionClose}
            scroll='paper'
            fullWidth
            maxWidth='md'
            aria-labelledby='Meeting Discussion Item Dialog'
          >
            <DialogTitle id='basic-info-dialog' className={formClasses.dialogHeaderFooter}>
              Planning Meeting 1 Discussion Notes
            </DialogTitle>
            <DialogContent>
              <MeetingDiscussionDialog
                handleClose={handleMeetingDiscussionClose}
                saveForm={handleAddMeetingDiscussionItem}
                discussionNotes={discussionNotes}
                setDiscussionNotes={setDiscussionNotes}
                auth={auth}
                meetingDiscussionItemSubmittedAttempted={meetingDiscussionItemSubmittedAttempted}
                setMeetingDiscussionItemSubmittedAttempted={setMeetingDiscussionItemSubmittedAttempted}
              />
            </DialogContent>
          </Dialog>
        )}
        {isAdminOrFHT(auth.roles) && (
          <>
            <summary style={{ marginBottom: '30px', textAlign: 'left', width: '100%', padding: '10px', border: '2px solid ' + btNavyBlueColor }}>
              <section align='left' className={formClasses.meetingDiscussionTitle}>
                <span style={{ width: '75%' }}>
                  Specialists Notes: Items to address during the planning meeting{' '}
                  {Array.isArray(discussionNoteUpdates) && discussionNoteUpdates.length > 0 && (
                    <Tooltip
                      title={
                        discussionNoteUpdates
                          ? typeof discussionNoteUpdates === 'object'
                            ? formatDiscussionUpdateData(discussionNoteUpdates)
                            : ''
                          : ''
                      }
                      placement='top'
                      classes={{ tooltip: formClasses.discussionTooltip }}
                    >
                      <InfoIcon
                        style={{ color: btNavyBlueColor, marginLeft: '5px', verticalAlign: 'middle', cursor: 'pointer' }}
                        className={formClasses.unprintedContent}
                      />
                    </Tooltip>
                  )}
                </span>
                <span style={{ width: '40%', textAlign: 'right' }} className={formClasses.unprintedContent}>
                  <Button
                    className={formClasses.meetingDiscussionButton}
                    color='primary'
                    variant='contained'
                    onClick={() => editDiscussionNotes(true)}
                    disabled={!isAdmin(auth.roles)}
                    style={{ margin: '0 0 7px 0' }}
                  >
                    Edit Notes
                  </Button>
                </span>
              </section>

              <div className={formClasses.meetingDiscussionNotes}>{discussionNotes || '(No discussion notes yet)'}</div>
            </summary>
          </>
        )}
        {store.senior && (
          <summary>
            <section align='left' className={formClasses.unprintedContent} style={{ width: '95%', textAlign: 'center', marginTop: '15px' }}>
              NOTE: This Planning Form - Meeting #1 will be automatically saved every five minutes.
              <br />
              <br />
            </section>
            <Grid container align='left' className={formClasses.unprintedContent}>
              <Grid item xs={12} align='center' className={formClasses.textRowLabelSpacer} style={{ minHeight: 45 }}>
                <aside className={formClasses.statusRow}>Current Status: {titleize(displayStatus(form.status))}</aside>
                {(isStudent(auth.roles) || isAlumni(auth.roles)) && form.status === 'returned' && (
                  <aside className={formClasses.error}>
                    NOTE: This Planning Form was returned for additional information. Please make the necessary changes and re-submit.
                  </aside>
                )}
              </Grid>
            </Grid>
            <div className={formClasses.printedContent}>
              <aside style={{ fontWeight: 600, fontSize: '1.50rem', paddingBottom: 10 }} align='center'>
                Current Status: {titleize(displayStatus(form.status))}
              </aside>
              {(isStudent(auth.roles) || isAlumni(auth.roles)) && form.status === 'returned' && (
                <aside className={formClasses.error}>
                  NOTE: This Planning Form was returned for additional information. Please make the necessary changes and re-submit.
                </aside>
              )}
            </div>
            {(form.status === 'approved' || form.status === 'incomplete') && (
              <>
                <aside className={formClasses.totalScore} align='center'>
                  Planning Form Score: {getPlanningMeeting1TotalScore(form)}
                </aside>
              </>
            )}
          </summary>
        )}

        {shouldDisplayStatusNotes() && (
          <Grid item xs={12} align='left' className={formClasses.dispositionNote}>
            {form.dispositionReason && form.dispositionReason.length > 0 && (
              <React.Fragment>
                <summary style={{ fontWeight: 600 }}>Notes Regarding Status</summary>
                <summary>{form.dispositionReason}</summary>
              </React.Fragment>
            )}
          </Grid>
        )}

        {store.senior && (
          <Grid container key={store.senior.userId} align='left'>
            <Grid container direction='row'>
              <Grid item xs={12} align='left' className={formClasses.firstSectionSpacer}>
                <section align='left' className={formClasses.firstSectionTitle}>
                  General Information
                </section>
              </Grid>
            </Grid>
            {store.senior && <GeneralInformation senior={store.senior} form={form} setForm={setForm} auth={auth} />}
            <Grid container direction='row' className={formClasses.sectionSpacer}>
              <Grid item xs={12} align='left'>
                <section align='left' className={formClasses.sectionTitle} style={{ width: '90%' }}>
                  When exploring my options, the following are my preliminary plans after graduation.
                </section>
              </Grid>
            </Grid>
            <Grid container direction='row' className={formClasses.smallRowSpacer} />
            <FallPlan form={form} setForm={setForm} auth={auth} />

            <Grid container direction='row' className={formClasses.sectionSpacer}>
              <Grid item xs={12} align='left'>
                <section align='left' className={formClasses.sectionTitle}>
                  Tasks I need to complete to move toward my plan.
                </section>
              </Grid>
            </Grid>
            <Grid container direction='row' className={formClasses.smallRowSpacer} />
            <Grid container direction='row' style={{ height: '185px', overflow: 'auto' }} className={formClasses.unprintedContent}>
              <Grid item xs={12} className={formClasses.textRowSpacer}>
                <ExpandingList
                  form={form}
                  setForm={setForm}
                  listname='tasks'
                  field1='name'
                  field1Cols={11}
                  field1MaxLength={500}
                  rowsRequired={5}
                  disabled={isFieldDisabled()}
                />
              </Grid>
            </Grid>
            <div className={formClasses.printedContent}>
              <div>
                <label>Tasks I need to complete to move toward my plan.</label>
              </div>
              <div>
                <summary className={formClasses.alignPrintedContent}>{createPrintedList(form.tasks)}</summary>
              </div>
              <br />
            </div>

            {/* Action Buttons */}
            <Grid container direction='row' className={formClasses.unprintedContent}>
              <Grid item xs={12} sm={8} md={5} lg={7} className={formClasses.buttonSpacer}>
                <div align='left'>
                  {(!form.status || form.status === 'created' || form.status === 'returned') && (
                    <Tooltip title='Click to submit your planning form for review.' placement='top'>
                      <Button
                        className={formClasses.floatingSubmitButton}
                        color='secondary'
                        variant='contained'
                        onClick={() => handleSubmitPlanningForm1()}
                        disabled={!isStudent(auth.roles) && !isAlumni(auth.roles)}
                        style={{ margin: '0 15px 15px 0', width: '110px' }}
                      >
                        Submit
                      </Button>
                    </Tooltip>
                  )}
                </div>
                <div align='right'>
                  {showSaveButton() ? (
                    <>
                      <Button
                        className={formClasses.floatingSaveButton}
                        variant='contained'
                        onClick={() => handleSaveReturn()}
                        disabled={!store.senior}
                        style={{ margin: '0 140px 15px 0', width: '110px' }}
                      >
                        {isFHT(auth.roles) ? 'Close' : 'Cancel'}
                      </Button>
                      <Button
                        className={formClasses.floatingSaveButton}
                        color='primary'
                        variant='contained'
                        onClick={() => handleSavePlanningForm1(true)}
                        disabled={!store.senior || isFHT(auth.roles)}
                        style={{ margin: '0 15px 15px 0', width: '110px' }}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button
                      className={formClasses.floatingSaveButton}
                      variant='contained'
                      onClick={() => handleSaveReturn()}
                      disabled={!store.senior}
                      style={{ margin: '0 15px 15px 0', width: '110px' }}
                    >
                      {isFHT(auth.roles) ? 'Close' : 'Cancel'}
                    </Button>
                  )}
                </div>
              </Grid>
            </Grid>

            {/* Submitted Date */}
            {form.status === 'submitted' && form.submitted_date && (
              <Grid container direction='row'>
                <Grid item xs={12} align='left' className={formClasses.firstSectionSpacer}>
                  <section align='left' className={formClasses.submittedInfo}>
                    <label>Planning Form Submitted:</label> {form.submitted_date}
                  </section>
                </Grid>
              </Grid>
            )}

            {/* Meeting Notes */}
            {!isStudent(auth.roles) && !isAlumni(auth.roles) && !isFHT(auth.roles) && (
              <React.Fragment>
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} sm={12} md={6} align='left'>
                    <section align='left' className={formClasses.sectionTitle}>
                      Meeting Notes
                    </section>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} sm={8} md={5} lg={7} className={formClasses.textArea5RowSpacer}>
                    <FormControl fullWidth>
                      <TextField
                        id='meetingComment'
                        ref={meetingCommentRef}
                        size='small'
                        hiddenLabel
                        variant='outlined'
                        value={addMeetingNote}
                        onChange={(event) => setAddMeetingNote(event.target.value)}
                        disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                        InputProps={{
                          classes: { input: formClasses.textField },
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={2} className={formClasses.textArea5RowSpacer}>
                    <Button
                      color='primary'
                      variant='contained'
                      onClick={() => handleAddMeetingComment()}
                      disabled={!addMeetingNote}
                      style={{ margin: '0 0 0 15px' }}
                    >
                      Add Note
                    </Button>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} className={formClasses.textRowSpacer}>
                    <aside className={formClasses.meetingSaveNote}>
                      NOTE: Meeting notes are not visible to students. After adding a note you MUST click "SAVE" to save the Planning Form.
                    </aside>
                  </Grid>
                </Grid>
                <summary
                  className={formClasses.unprintedContent}
                  style={{ height: '300px', width: '90%', border: '1px solid #808080', overflowY: 'scroll' }}
                >
                  <Grid container direction='row' className={formClasses.sectionSpacer}>
                    <Grid item xs={12} sm={12} md={9}>
                      {form.meetingComments &&
                        [...form.meetingComments]
                          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                          .map((comment, idx) => {
                            return (
                              <table style={{ width: '100%', marginBottom: '20px', fontSize: '0.90rem' }} key={idx}>
                                <tr style={{ margin: '5px 0' }}>
                                  <td style={{ width: '100%', verticalAlign: 'top' }}>
                                    <aside>
                                      <label>Logged at</label> {comment.timestamp}
                                    </aside>
                                    <aside>
                                      <label>By</label> {comment.user}
                                    </aside>
                                    <summary>{comment.text}</summary>
                                  </td>
                                </tr>
                              </table>
                            );
                          })}
                    </Grid>
                  </Grid>
                </summary>
                <summary className={formClasses.printedContent} style={{ width: '90%' }}>
                  {form.meetingComments &&
                    [...form.meetingComments]
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((comment, idx) => {
                        return (
                          <table style={{ width: '100%', marginBottom: '20px', fontSize: '0.90rem' }} key={idx}>
                            <tr style={{ margin: '5px 0' }}>
                              <td style={{ width: '100%', verticalAlign: 'top' }}>
                                <aside>
                                  <label>Logged at</label> {comment.timestamp}
                                </aside>
                                <aside>
                                  <label>By</label> {comment.user}
                                </aside>
                                <summary>{comment.text}</summary>
                              </td>
                            </tr>
                          </table>
                        );
                      })}
                </summary>
              </React.Fragment>
            )}

            <Grid container direction='row' className={formClasses.sectionSpacer}>
              <Grid item xs={12} sm={12} md={6} align='left'>
                {isStudent(auth.roles) || isAlumni(auth.roles) ? (
                  <section align='left' className={formClasses.disabledSectionTitle}>
                    Comments
                  </section>
                ) : (
                  <section align='left' className={formClasses.sectionTitle}>
                    Comments
                  </section>
                )}
              </Grid>
            </Grid>
            {!isStudent(auth.roles) && !isAlumni(auth.roles) && (
              <React.Fragment>
                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} sm={8} md={5} lg={7} className={formClasses.textArea5RowSpacer}>
                    <FormControl fullWidth>
                      <TextField
                        id='comment'
                        ref={commentRef}
                        size='small'
                        hiddenLabel
                        variant='outlined'
                        value={addNote}
                        onChange={(event) => setAddNote(event.target.value)}
                        disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                        InputProps={{
                          classes: { input: formClasses.textField },
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={2} className={formClasses.textArea5RowSpacer}>
                    <Button
                      color='primary'
                      variant='contained'
                      onClick={() => handleAddComment()}
                      disabled={!addNote}
                      style={{ margin: '0 0 0 15px' }}
                    >
                      Add Comment
                    </Button>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} sm={10} md={9} className={formClasses.textArea5RowSpacer}>
                    <aside className={formClasses.commentSaveNote}>
                      NOTE: Comments should be used to advise students. They will be seen by the students. After adding a comment you MUST click
                      "SAVE" to save the Planning Form.
                    </aside>
                  </Grid>
                </Grid>
              </React.Fragment>
            )}

            <summary
              className={formClasses.unprintedContent}
              style={{ height: '300px', width: '90%', border: '1px solid #808080', overflowY: 'scroll', marginBottom: 35 }}
            >
              <Grid container direction='row' className={formClasses.sectionSpacer}>
                <Grid item xs={12} sm={12} md={9}>
                  {form.comments &&
                    [...form.comments]
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((comment, idx) => {
                        return (
                          <table style={{ width: '100%', marginBottom: '20px', fontSize: '0.90rem' }} key={idx}>
                            <tr style={{ margin: '5px 0' }}>
                              <td style={{ width: '100%', verticalAlign: 'top' }}>
                                <aside>
                                  <label>Logged at</label> {comment.timestamp}
                                </aside>
                                <aside>
                                  <label>By</label> {comment.user}
                                </aside>
                                <summary>{comment.text}</summary>
                              </td>
                            </tr>
                          </table>
                        );
                      })}
                </Grid>
              </Grid>
            </summary>
            <summary className={formClasses.printedContent} style={{ width: '90%', marginBottom: 35 }}>
              {form.comments &&
                [...form.comments]
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((comment, idx) => {
                    return (
                      <table style={{ width: '100%', marginBottom: '20px', fontSize: '0.90rem' }} key={idx}>
                        <tr style={{ margin: '5px 0' }}>
                          <td style={{ width: '100%', verticalAlign: 'top' }}>
                            <aside>
                              <label>Logged at</label> {comment.timestamp}
                            </aside>
                            <aside>
                              <label>By</label> {comment.user}
                            </aside>
                            <summary>{comment.text}</summary>
                          </td>
                        </tr>
                      </table>
                    );
                  })}
            </summary>
          </Grid>
        )}

        {!isStudent(auth.roles) && !isAlumni(auth.roles) && !isFHT(auth.roles) && (
          <Grid container direction='row' className={formClasses.unprintedContent}>
            <Grid item xs={12} align='left'>
              <FormControlLabel
                style={{ marginLeft: '10px' }}
                control={
                  <Tooltip
                    title='Student has not submitted the planning form, but we need to score the other pieces required for incentive payments.'
                    placement='top'
                  >
                    <Checkbox
                      color='default'
                      disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                      checked={showIncomplete}
                      onChange={() => {
                        markFormIncomplete();
                      }}
                    />
                  </Tooltip>
                }
                label='Show Admin Form for Incomplete Grading'
              />
            </Grid>
          </Grid>
        )}

        {/* Administrative Disposition Panel */}
        {showDisposition() && (
          <Grid
            container
            direction='row'
            className={formClasses.unprintedContent}
            style={{ marginTop: 20, padding: 20, border: '1px solid darkgreen', background: '#dcf5dc' }}
          >
            <Grid item xs={12} align='center'>
              <label style={{ fontWeight: 600 }}>Successful Futures Administration Only</label>
            </Grid>
            <Grid item xs={12} sm={5} md={3} lg={2} className={formClasses.textRowLabelSpacer} align='left'>
              <label style={{ fontWeight: 600, color: '#000000' }}>Planning Form 1 Status</label>
            </Grid>
            <Grid item xs={12} sm={12} md={5} lg={6} className={formClasses.textArea5RowSpacer}>
              <FormControl fullWidth>
                <RadioGroup
                  row
                  aria-label='approve planning form1'
                  name='approve-planningform1'
                  horizontal='true'
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value })}
                  disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                >
                  {!showIncomplete && (
                    <FormControlLabel
                      value='returned'
                      className={formClasses.marginRight40}
                      control={<Radio color='default' disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)} />}
                      label={
                        <span className={formClasses.radioLabel} style={{ fontWeight: 500, color: '#000000' }}>
                          Returned
                        </span>
                      }
                    />
                  )}
                  {!showIncomplete && (
                    <FormControlLabel
                      value='approved'
                      className={formClasses.marginRight40}
                      control={<Radio color='default' disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)} />}
                      label={
                        <span className={formClasses.radioLabel} style={{ fontWeight: 500, color: '#000000' }}>
                          Approved
                        </span>
                      }
                    />
                  )}
                  <FormControlLabel
                    value='incomplete'
                    className={formClasses.marginRight40}
                    control={<Radio color='default' disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)} />}
                    label={
                      <span className={formClasses.radioLabel} style={{ fontWeight: 500, color: '#000000' }}>
                        Incomplete
                      </span>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4} className={formClasses.textArea5RowSpacer} align='right'>
              <FormControlLabel
                style={{ marginLeft: '10px' }}
                control={
                  <Tooltip title='Student needs an additional senior planning meeting to address shortcomings in their current plan.' placement='top'>
                    <Checkbox
                      color='default'
                      disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                      checked={form.followup ? form.followup : false}
                      onChange={(ev) => {
                        if (ev.target.checked) {
                          setForm({ ...form, followup: true });
                        } else {
                          setForm({ ...form, followup: ev.target.checked });
                        }
                      }}
                    />
                  </Tooltip>
                }
                label='Needs Follow-up'
              />
            </Grid>
            {(form.status === 'returned' || form.status === 'approved') && (
              <React.Fragment>
                <Grid item xs={12} className={formClasses.textRowSpacer}>
                  <FormControl fullWidth>
                    <TextField
                      id='disposition-reason'
                      size='small'
                      fullWidth
                      label={`Enter any notes for status '${titleize(form.status)}'`}
                      variant='outlined'
                      value={form.dispositionReason}
                      onChange={(event) => setForm({ ...form, dispositionReason: event.target.value })}
                      disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                      InputProps={{ maxLength: 500, classes: { input: formClasses.textField } }}
                      className={formClasses.root}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} className={formClasses.textRowSpacer}>
                  <label className={formClasses.note}>
                    {form.status === 'returned'
                      ? 'This reason will be displayed to the student when they view the returned planning form.'
                      : 'This reason will be displayed to the student when they view the approved planning form.'}
                  </label>
                </Grid>
              </React.Fragment>
            )}

            <Scoring form={form} setForm={setForm} disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)} />
          </Grid>
        )}
        <RequiredInfoDialog
          title={requiredFieldsTitle}
          introduction={requiredFieldsIntro}
          content={requiredFields}
          showDialog={showRequiredFieldsDialog}
          handleClose={handleRequiredInfoClose}
          handleContinue={handleRequiredInfoContinue}
          optional={optionalFieldOnly}
        />
        <Snackbar
          ContentProps={{
            classes: {
              root: formClasses.warning,
            },
          }}
          open={showWarning}
          autoHideDuration={6000}
          onClose={handleClose}
          message={warning}
          action={action}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        />
        <Snackbar
          ContentProps={{
            classes: {
              root: formClasses.errorAlert,
            },
          }}
          open={showError}
          autoHideDuration={10000}
          onClose={handleErrorClose}
          message={error}
          action={errorAction}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </React.Fragment>
    );
  })
);

SeniorPlanning1.propTypes = {
  handleSaveReturn: PropTypes.func,
};

export default SeniorPlanning1;
